import React, { useState } from 'react';
import {
  User,
  KeyRound,
  Save,
  Shield,
  Bell,
  CheckCircle2,
  AlertCircle,
  Laptop,
  Smartphone,
  Eye,
  EyeOff,
  LogOut,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuthStore } from '@/stores/useAuthStore';
import { toast } from 'sonner';
import { useGetApiUsersMe, usePutApiUsersMeSettings, getGetApiUsersMeQueryKey } from '@/api/generated/users/users';
import { useQueryClient } from '@tanstack/react-query';
import type { UserProfileDto } from '@/api/generated/model';

export const ProfilePage: React.FC = () => {
  const { user, login, token } = useAuthStore();
  const queryClient = useQueryClient();
  const { data: rawUserProfile } = useGetApiUsersMe();
  const userProfile = rawUserProfile as unknown as UserProfileDto;
  const { mutate: updateSettings } = usePutApiUsersMeSettings();

  // Profile Form State
  const [fullName, setFullName] = useState(userProfile?.fullName || user?.fullName || 'Diego Fernandes');
  const [email, setEmail] = useState(userProfile?.email || user?.email || 'admin@taleshq.com');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Password Form State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const handleSettingsChange = (newSettings: Partial<NonNullable<typeof userProfile>['settings']>) => {
    if (!userProfile) return;
    
    const currentSettings = userProfile.settings || {
      inAppNotificationsEnabled: true,
      desktopNotificationsEnabled: false,
      soundEnabled: true,
    };
    
    // Request permission if enabling desktop notifications
    if (newSettings?.desktopNotificationsEnabled && Notification.permission !== 'granted') {
      Notification.requestPermission();
    }

    const updatedSettings = { ...currentSettings, ...newSettings };

    // Optimistic Update
    queryClient.setQueryData(getGetApiUsersMeQueryKey(), {
      ...userProfile,
      settings: updatedSettings,
    });

    updateSettings(
      { data: { settings: updatedSettings } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetApiUsersMeQueryKey() });
          toast.success('Configura??es atualizadas');
        },
        onError: () => {
          queryClient.invalidateQueries({ queryKey: getGetApiUsersMeQueryKey() });
          toast.error('Erro ao atualizar configura??es');
        }
      }
    );
  };

  const getInitials = (name?: string) => {
    if (!name) return 'DF';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const calculatePasswordStrength = (pass: string) => {
    if (!pass) return 0;
    let score = 0;
    if (pass.length >= 8) score += 25;
    if (/[A-Z]/.test(pass)) score += 25;
    if (/[0-9]/.test(pass)) score += 25;
    if (/[^A-Za-z0-9]/.test(pass)) score += 25;
    return score;
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error('Full name cannot be empty');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSavingProfile(true);
    setTimeout(() => {
      if (user && token) {
        login(token, {
          ...user,
          fullName: fullName.trim(),
          email: email.trim(),
        });
      }
      setIsSavingProfile(false);
      toast.success('Merchant profile information updated successfully');
    }, 400);
  };

  const handleUpdatePassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Todos os campos de senha são obrigatórios');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('A nova senha e a senha de confirmação não coincidem');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('A nova senha não pode ser igual à sua senha atual');
      return;
    }

    setIsUpdatingPassword(true);
    setTimeout(() => {
      setIsUpdatingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Senha alterada com sucesso! Mantenha suas credenciais seguras.');
    }, 600);
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <User className="h-5 w-5 text-primary" />
          Conta e Segurança
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
          Gerencie suas credenciais pessoais, segurança de senha e alertas de notificação
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Profile & Password Forms (8 cols) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader className="pb-3">
              <div className="space-y-0.5">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  Informações Pessoais
                </CardTitle>
                <CardDescription className="text-xs">
                  Atualize seu nome de exibição e e-mail de login
                </CardDescription>
              </div>
            </CardHeader>

            <form onSubmit={handleSaveProfile}>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 py-2 border-b border-border/50">
                  <Avatar className="h-14 w-14 border border-border shadow-sm">
                    <AvatarFallback className="bg-primary/10 text-primary text-base font-bold">
                      {getInitials(fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-foreground">{fullName}</p>
                    <p className="text-xs text-muted-foreground">{email}</p>
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                      <CheckCircle2 className="h-3 w-3" />
                      E-mail Verificado e Autenticado
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="profFullName" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Nome Completo
                    </Label>
                    <Input
                      id="profFullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="h-9 text-xs font-medium"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="profEmail" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Endereço de E-mail
                    </Label>
                    <Input
                      id="profEmail"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-9 text-xs font-medium"
                      required
                    />
                  </div>
                </div>
              </CardContent>

              <CardFooter className="pt-3 border-t border-border/50 flex justify-end">
                <Button type="submit" size="sm" disabled={isSavingProfile} className="gap-1.5 shadow-sm text-xs font-medium">
                  {isSavingProfile ? <LoadingSpinner size="sm" /> : <Save className="h-3.5 w-3.5" />}
                  <span>Salvar Perfil</span>
                </Button>
              </CardFooter>
            </form>
          </Card>

          {/* Password Security */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <CardTitle className="text-base flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-primary" />
                    Security & Password
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Ensure your account is protected with a strong, distinct password
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1"
                >
                  {showPasswords ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  <span>{showPasswords ? 'Hide' : 'Show'}</span>
                </Button>
              </div>
            </CardHeader>

            <form onSubmit={handleUpdatePassword}>
              <CardContent className="space-y-4">
                {passwordError && (
                  <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{passwordError}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="currPass" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Senha Atual
                  </Label>
                  <Input
                    id="currPass"
                    type={showPasswords ? 'text' : 'password'}
                    placeholder="Digite a senha atual"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="h-9 text-xs"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="newPass" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Nova Senha
                    </Label>
                    <Input
                      id="newPass"
                      type={showPasswords ? 'text' : 'password'}
                      placeholder="Mínimo 8 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-9 text-xs"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confPass" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Confirmar Nova Senha
                    </Label>
                    <Input
                      id="confPass"
                      type={showPasswords ? 'text' : 'password'}
                      placeholder="Redigite a nova senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-9 text-xs"
                      required
                    />
                  </div>
                </div>

                {/* Password Strength Meter */}
                {newPassword && (
                  <div className="space-y-1.5 pt-1">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-muted-foreground">Password strength:</span>
                      <span className="font-semibold text-foreground">
                        {passwordStrength < 50 ? 'Weak' : passwordStrength < 75 ? 'Good' : 'Strong'}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          passwordStrength < 50
                            ? 'bg-destructive'
                            : passwordStrength < 75
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                        }`}
                        style={{ width: `${passwordStrength}%` }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>

              <CardFooter className="pt-3 border-t border-border/50 flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  disabled={isUpdatingPassword}
                  className="gap-1.5 shadow-sm text-xs font-medium"
                >
                  {isUpdatingPassword ? <LoadingSpinner size="sm" /> : <KeyRound className="h-3.5 w-3.5" />}
                  <span>Update Password</span>
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>

        {/* Right Column: Notifications & Active Sessions (4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Notification Preferences */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                Alertas de Notifica??o
              </CardTitle>
              <CardDescription className="text-xs">
                Prefer?ncias de alertas em tempo real para eventos de bate-papo da loja
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-xs">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium text-foreground">Transferência para Atendente</p>
                  <p className="text-[11px] text-muted-foreground">
                    Alertar quando um visitante pedir para falar com um atendente
                  </p>
                </div>
                <Switch
                  checked={userProfile?.settings?.inAppNotificationsEnabled ?? true}
                  onCheckedChange={(checked) =>
                    handleSettingsChange({ inAppNotificationsEnabled: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium text-foreground">Efeitos Sonoros</p>
                  <p className="text-[11px] text-muted-foreground">
                    Reproduzir som ao receber novas mensagens
                  </p>
                </div>
                <Switch
                  checked={userProfile?.settings?.soundEnabled ?? true}
                  onCheckedChange={(checked) =>
                    handleSettingsChange({ soundEnabled: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <p className="font-medium text-foreground">Alertas Push na ?rea de Trabalho</p>
                  <p className="text-[11px] text-muted-foreground">
                    Notifica??es do navegador quando a aba estiver em segundo plano
                  </p>
                </div>
                <Switch
                  checked={userProfile?.settings?.desktopNotificationsEnabled ?? false}
                  onCheckedChange={(checked) =>
                    handleSettingsChange({ desktopNotificationsEnabled: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Active Sessions & Security */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                Sessões Ativas
              </CardTitle>
              <CardDescription className="text-xs">
                Dispositivos atualmente logados nesta conta de lojista
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/60 bg-muted/20">
                <div className="flex items-center gap-2.5">
                  <Laptop className="h-4 w-4 text-emerald-500" />
                  <div>
                    <p className="font-semibold text-foreground">Chrome no Windows</p>
                    <p className="text-[10px] text-muted-foreground">Sessão Ativa Atual • São Paulo, BR</p>
                  </div>
                </div>
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]">
                  Online
                </Badge>
              </div>

              <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/40 text-muted-foreground">
                <div className="flex items-center gap-2.5">
                  <Smartphone className="h-4 w-4 text-muted-foreground/70" />
                  <div>
                    <p className="font-medium text-foreground">Aplicativo Móvel iOS</p>
                    <p className="text-[10px] text-muted-foreground">Última atividade há 3 horas</p>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="pt-2 border-t border-border/50">
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => toast.success('Desconectado de todas as outras sessões')}
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Sair de Outras Sessões</span>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
